import React, { useState, useCallback } from 'react'
import { PassareAcquaintance, syncLinkManuallyByUniqueIdentifier } from '../../../../../mogul/core/_requests'
import { APIError } from '../../../../../app/modules/auth'

interface BulkSyncModalProps {
    isOpen: boolean
    onClose: () => void
    leadsToSync: PassareAcquaintance[]
    onSyncComplete: () => void
}

interface SyncProgress {
    uniqueIdentifier: string
    status: 'pending' | 'syncing' | 'completed' | 'error'
    error?: string
}

export default function BulkSyncModal({ isOpen, onClose, leadsToSync, onSyncComplete }: BulkSyncModalProps) {
    const [syncing, setSyncing] = useState(false)
    const [syncProgress, setSyncProgress] = useState<SyncProgress[]>([])
    const [currentIndex, setCurrentIndex] = useState(0)

    const handleStartSync = useCallback(async () => {
        if (leadsToSync.length === 0) return

        setSyncing(true)
        setCurrentIndex(0)

        // Initialize progress tracking
        const initialProgress: SyncProgress[] = leadsToSync.map(lead => ({
            uniqueIdentifier: lead.unique_identifier,
            status: 'pending'
        }))
        setSyncProgress(initialProgress)

        // Process each lead sequentially
        for (let i = 0; i < leadsToSync.length; i++) {
            const lead = leadsToSync[i]
            setCurrentIndex(i)

            // Update status to syncing
            setSyncProgress(prev => prev.map(p =>
                p.uniqueIdentifier === lead.unique_identifier
                    ? { ...p, status: 'syncing' }
                    : p
            ))

            try {
                await syncLinkManuallyByUniqueIdentifier(lead.unique_identifier)

                // Update status to completed
                setSyncProgress(prev => prev.map(p =>
                    p.uniqueIdentifier === lead.unique_identifier
                        ? { ...p, status: 'completed' }
                        : p
                ))
            } catch (error) {
                console.error('Sync error:', error)
                const errorMessage = error instanceof APIError ? error.message : 'Failed to sync'

                // Update status to error
                setSyncProgress(prev => prev.map(p =>
                    p.uniqueIdentifier === lead.unique_identifier
                        ? { ...p, status: 'error', error: errorMessage }
                        : p
                ))
            }
        }

        setSyncing(false)
        onSyncComplete()
    }, [leadsToSync, onSyncComplete])

    const handleClose = () => {
        if (!syncing) {
            onClose()
            setSyncProgress([])
            setCurrentIndex(0)
        }
    }

    // Prevent navigation away during sync
    React.useEffect(() => {
        if (syncing) {
            const handleBeforeUnload = (e: BeforeUnloadEvent) => {
                e.preventDefault()
                e.returnValue = 'Sync in progress. Are you sure you want to leave?'
                return e.returnValue
            }

            window.addEventListener('beforeunload', handleBeforeUnload)
            return () => window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [syncing])

    if (!isOpen) return null

    const completedCount = syncProgress.filter(p => p.status === 'completed').length
    const errorCount = syncProgress.filter(p => p.status === 'error').length

    return (
        <div className="modal fade show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Bulk Sync to 360
                        </h5>
                        {!syncing && (
                            <button
                                type="button"
                                className="btn-close"
                                onClick={handleClose}
                            ></button>
                        )}
                    </div>
                    <div className="modal-body">
                        {!syncing && syncProgress.length === 0 && (
                            <>
                                <p className="mb-4">
                                    You are about to sync <strong>{leadsToSync.length}</strong> unsynced leads to the 360 platform.
                                </p>
                                <div className="alert alert-warning">
                                    <strong>Warning:</strong> This process cannot be undone. Once started, the page will be locked until completion.
                                </div>
                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Organization</th>
                                                <th>Deceased</th>
                                                <th>Lead Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leadsToSync.map(lead => (
                                                <tr key={lead.unique_identifier}>
                                                    <td>
                                                        {lead.passare_organization.organization_name || 'Unknown'}
                                                        {lead.case_item.branch_name && (
                                                            <span className="text-muted"> ({lead.case_item.branch_name})</span>
                                                        )}
                                                    </td>
                                                    <td>{lead.getDecedentFullName()}</td>
                                                    <td>
                                                        {lead.public_data.acquaintance_result
                                                            ? `${lead.public_data.acquaintance_result.first_name} ${lead.public_data.acquaintance_result.last_name}`
                                                            : '-'}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}

                        {(syncing || syncProgress.length > 0) && (
                            <>
                                <div className="mb-4">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span>Progress:</span>
                                        <span>{completedCount + errorCount} / {leadsToSync.length}</span>
                                    </div>
                                    <div className="progress">
                                        <div
                                            className="progress-bar"
                                            style={{
                                                width: `${((completedCount + errorCount) / leadsToSync.length) * 100}%`
                                            }}
                                        ></div>
                                    </div>
                                    {errorCount > 0 && (
                                        <div className="text-danger mt-2">
                                            {errorCount} error(s) occurred during sync
                                        </div>
                                    )}
                                </div>

                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>Status</th>
                                                <th>Organization</th>
                                                <th>Deceased</th>
                                                <th>Lead Name</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {leadsToSync.map((lead, index) => {
                                                const progress = syncProgress.find(p => p.uniqueIdentifier === lead.unique_identifier)
                                                return (
                                                    <tr key={lead.unique_identifier} className={index === currentIndex ? 'table-active' : ''}>
                                                        <td>
                                                            {progress?.status === 'pending' && <span className="badge badge-light">Pending</span>}
                                                            {progress?.status === 'syncing' && <span className="badge badge-warning">Syncing...</span>}
                                                            {progress?.status === 'completed' && <span className="badge badge-success">✓ Done</span>}
                                                            {progress?.status === 'error' && (
                                                                <span className="badge badge-danger" title={progress.error}>✗ Error</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {lead.passare_organization.organization_name || 'Unknown'}
                                                            {lead.case_item.branch_name && (
                                                                <span className="text-muted"> ({lead.case_item.branch_name})</span>
                                                            )}
                                                        </td>
                                                        <td>{lead.getDecedentFullName()}</td>
                                                        <td>
                                                            {lead.public_data.acquaintance_result
                                                                ? `${lead.public_data.acquaintance_result.first_name} ${lead.public_data.acquaintance_result.last_name}`
                                                                : '-'}
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="modal-footer">
                        {!syncing && syncProgress.length === 0 && (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-warning"
                                    onClick={handleStartSync}
                                    disabled={leadsToSync.length === 0}
                                >
                                    Start Sync ({leadsToSync.length} leads)
                                </button>
                            </>
                        )}

                        {syncing && (
                            <button type="button" className="btn btn-warning" disabled>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Syncing... ({completedCount + errorCount} / {leadsToSync.length})
                            </button>
                        )}

                        {!syncing && syncProgress.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleClose}
                            >
                                Close ({completedCount} synced, {errorCount} errors)
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}